import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import PrintIcon from '@material-ui/icons/Print';
import WarningIcon from '@material-ui/icons/Warning';
import {
  TOUR_PENDING_KEY,
  DUP_MERGE_EVENT,
  driverPrintedIndicator,
  driverDupWarning,
  driverDupMerge,
} from '../Shared/WhatsNew/tours';

const ONBOARDING_IS_ADMIN_KEY = 'onboardingIsAdmin';

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(2) },
  headCell: {
    backgroundColor: '#4DB6D4',
    color: '#fff',
    fontWeight: 700,
    fontSize: 13,
    padding: '8px 12px',
    borderBottom: 'none',
  },
  cell: { fontSize: 13, padding: '8px 12px' },
  iconCell: { whiteSpace: 'nowrap', padding: '6px 8px' },
  evenRow: { backgroundColor: '#fafafa' },
  // merge dialog
  scoreBar: { display: 'flex', gap: 8, padding: '0 0 16px', flexWrap: 'wrap' },
  scoreChip: {
    background: '#2e7d32',
    color: '#fff',
    borderRadius: 12,
    padding: '3px 12px',
    fontSize: 12,
    fontWeight: 700,
  },
  scoreLabel: { fontWeight: 400, marginRight: 4, opacity: 0.85 },
  cardSelected: { border: '2px solid #1976d2 !important' },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '12px 16px',
    color: '#fff',
  },
  fieldRow: { padding: '6px 0', borderBottom: '1px solid #f0f0f0' },
  fieldLabel: { fontSize: 11, color: '#999', marginBottom: 2 },
  dialogTitle: {
    background: '#142E3E',
    color: '#fff',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
}));

const COLUMNS = [
  { key: 'manufacturer',  label: 'Manufacturer' },
  { key: 'distributor',   label: 'Distributor' },
  { key: 'privateName',   label: 'Private Style Name' },
  { key: 'styleName',     label: 'Style Name' },
  { key: 'styleNum',      label: 'Style Number' },
  { key: 'matClass',      label: 'Material Class' },
  { key: 'matSubClass',   label: 'Material Sub Class' },
  { key: 'size',          label: 'Size' },
  { key: 'backing',       label: 'Backing' },
];

const MOCK_PRODUCTS = [
  {
    id: '1', manufacturer: 'DALTILE', distributor: 'DALTILE', privateName: '',
    styleName: "P'ZAZZ FIELD TILE FLOOR 12X12 GROUP 1", styleNum: '0880',
    matClass: 'CERAMIC TILE', matSubClass: '', size: '12X12', backing: '',
    hasPrinted: true, hasDuplicate: true, dupScore: 100,
  },
  {
    id: '2', manufacturer: 'DALTILE', distributor: 'DALTILE', privateName: '',
    styleName: "P'ZAZZ FIELD TILE FLOOR 12X12 GROUP 2", styleNum: '0880',
    matClass: 'CERAMIC TILE', matSubClass: '', size: '12X12', backing: '',
    hasPrinted: false, hasDuplicate: true, dupScore: 100,
  },
  {
    id: '3', manufacturer: 'SHAW', distributor: 'SHAW', privateName: '',
    styleName: 'DISTINCTION HARDWOOD OAK NATURAL', styleNum: '5000',
    matClass: 'HARDWOOD', matSubClass: '', size: '3.25"', backing: '',
    hasPrinted: false, hasDuplicate: false,
  },
  {
    id: '4', manufacturer: 'MOHAWK', distributor: 'MOHAWK', privateName: '',
    styleName: 'REVWOOD PLUS DOGWOOD RIDGE', styleNum: 'REV-01',
    matClass: 'LAMINATE', matSubClass: '', size: '5"', backing: '',
    hasPrinted: false, hasDuplicate: false,
  },
];

const MERGE_SCORES = [
  ['Overall', 100], ['Style #', 100], ['Name', 98], ['Mfg', 100], ['Size', 95],
];

const PRODUCT_A_FIELDS = [
  ['Style Name',     "P'ZAZZ FIELD TILE FLOOR 12X12 GROUP 1"],
  ['Style Number',   '0880'],
  ['Distributor',    'DALTILE'],
  ['Material Class', 'CERAMIC TILE'],
  ['Size',           '12X12'],
];

const PRODUCT_B_FIELDS = [
  ['Style Name',     "P'ZAZZ FIELD TILE FLOOR 12X12 GROUP 2"],
  ['Style Number',   '0880'],
  ['Distributor',    'DALTILE'],
  ['Material Class', 'CERAMIC TILE'],
  ['Size',           '12X12'],
];

export default function TourProductsPage() {
  const classes = useStyles();
  const history = useHistory();
  const [dupDialogOpen, setDupDialogOpen] = useState(false);
  const afterDoneRef = useRef(null);

  // Start the appropriate driver when this page mounts
  useEffect(() => {
    const key = sessionStorage.getItem(TOUR_PENDING_KEY);

    if (key === 'tour-printed') {
      sessionStorage.removeItem(TOUR_PENDING_KEY);
      setTimeout(() => driverPrintedIndicator(history), 500);

    } else if (key === 'tour-dup-product') {
      sessionStorage.removeItem(TOUR_PENDING_KEY);
      // What's New tour — after merge dialog, go home
      setTimeout(() => driverDupWarning(history, null), 500);

    } else if (key === 'onboarding-dup-product') {
      sessionStorage.removeItem(TOUR_PENDING_KEY);
      // Onboarding tour — after merge dialog, continue to settings or lead management
      const isAdmin = sessionStorage.getItem(ONBOARDING_IS_ADMIN_KEY) === '1';
      const afterDone = () => {
        if (isAdmin) {
          sessionStorage.setItem(TOUR_PENDING_KEY, 'onboarding-settings');
          history.push('/settings');
        } else {
          sessionStorage.setItem(TOUR_PENDING_KEY, 'onboarding-lead');
          history.push('/lead-management');
        }
      };
      setTimeout(() => driverDupWarning(history, afterDone), 500);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for the event dispatched by driverDupWarning when user clicks Next
  useEffect(() => {
    function handleOpen(e) {
      const rawAfterDone = e.detail.afterDone;
      // Wrap so the dialog is closed before continuing the tour
      const wrappedAfterDone = () => {
        setDupDialogOpen(false);
        if (rawAfterDone) {
          rawAfterDone();
        } else {
          sessionStorage.setItem(TOUR_PENDING_KEY, 'tour-complete');
          history.push('/home');
        }
      };
      afterDoneRef.current = wrappedAfterDone;
      setDupDialogOpen(true);
      setTimeout(() => driverDupMerge(history, wrappedAfterDone), 400);
    }
    window.addEventListener(DUP_MERGE_EVENT, handleOpen);
    return () => window.removeEventListener(DUP_MERGE_EVENT, handleOpen);
  }, [history]);

  return (
    <div className={classes.root}>
      {/* ── Product list table ── */}
      <Paper elevation={2} style={{ borderRadius: 8, overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', backgroundColor: '#142E3E' }}>
          <Typography variant="h4" style={{ color: '#fff' }}>Product List</Typography>
        </div>

        <TableContainer>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell className={classes.headCell} padding="checkbox">
                  <Checkbox style={{ color: 'rgba(255,255,255,0.5)' }} disabled size="small" />
                </TableCell>
                <TableCell className={classes.headCell}>More Details</TableCell>
                {COLUMNS.map((c) => (
                  <TableCell key={c.key} className={classes.headCell}>{c.label}</TableCell>
                ))}
                <TableCell className={classes.headCell}>Update</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {MOCK_PRODUCTS.map((p, i) => (
                <TableRow key={p.id} className={i % 2 === 1 ? classes.evenRow : ''}>
                  <TableCell padding="checkbox" className={classes.cell}>
                    <Checkbox disabled size="small" />
                  </TableCell>
                  <TableCell className={classes.iconCell}>
                    <IconButton size="small" disabled>
                      <KeyboardArrowDownIcon fontSize="small" />
                    </IconButton>
                    {p.hasPrinted && (
                      <Tooltip title="A QR code has been printed for this product">
                        <IconButton
                          id="tour-printed-icon"
                          size="small"
                          style={{ color: '#3949ab', marginLeft: 2 }}
                          disableRipple
                        >
                          <PrintIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {p.hasDuplicate && (
                      <Tooltip title={`Potential duplicate detected (${p.dupScore}% match) — click to review`}>
                        <IconButton
                          id={p.id === '1' ? 'tour-dup-warning-icon' : undefined}
                          size="small"
                          style={{ color: '#f57c00', marginLeft: 2 }}
                          disableRipple
                        >
                          <WarningIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                  {COLUMNS.map((c) => (
                    <TableCell key={c.key} className={classes.cell}>{p[c.key]}</TableCell>
                  ))}
                  <TableCell className={classes.cell}>
                    <Button variant="contained" size="small" disabled style={{ fontSize: 11 }}>
                      VIEW
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* ── Mock duplicate merge review dialog ── */}
      <Dialog open={dupDialogOpen} fullWidth maxWidth="xl">
        <DialogTitle disableTypography className={classes.dialogTitle}>
          <Typography variant="h6" style={{ color: '#fff' }}>
            Review &amp; Merge Products
          </Typography>
          <Button variant="outlined" size="small" style={{ color: '#fff', borderColor: 'rgba(255,255,255,0.5)' }} disabled>
            X
          </Button>
        </DialogTitle>

        <DialogContent dividers style={{ padding: 20 }}>
          {/* Score bar */}
          <div className={classes.scoreBar}>
            {MERGE_SCORES.map(([label, score]) => (
              <span key={label} className={classes.scoreChip}>
                <span className={classes.scoreLabel}>{label}</span>
                {score}%
              </span>
            ))}
          </div>

          {/* Two product cards */}
          <Grid container spacing={2}>
            {/* Product A — shown as selected (blue border) */}
            <Grid item xs={12} md={6}>
              <Paper
                id="tour-dup-card-a"
                elevation={4}
                className={classes.cardSelected}
                style={{ borderRadius: 8, overflow: 'hidden' }}
              >
                <div className={classes.cardHeader} style={{ background: '#1565C0' }}>
                  <div>
                    <Typography style={{ color: '#fff', fontWeight: 700 }}>DALTILE</Typography>
                    <Typography style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12 }}>
                      Last modified: 2025-03-15
                    </Typography>
                  </div>
                  <Button variant="outlined" size="small" style={{ color: '#fff', borderColor: '#fff', fontSize: 11 }} disabled>
                    ✓ Keeping this record
                  </Button>
                </div>
                <div style={{ padding: '0 16px 16px' }}>
                  {PRODUCT_A_FIELDS.map(([label, value]) => (
                    <div key={label} className={classes.fieldRow}>
                      <div className={classes.fieldLabel}>{label}</div>
                      <Typography variant="body2">{value}</Typography>
                    </div>
                  ))}
                </div>
              </Paper>
            </Grid>

            {/* Product B — unselected */}
            <Grid item xs={12} md={6}>
              <Paper elevation={1} style={{ borderRadius: 8, overflow: 'hidden', border: '2px solid transparent' }}>
                <div className={classes.cardHeader} style={{ background: '#546e7a' }}>
                  <div>
                    <Typography style={{ color: '#fff', fontWeight: 700 }}>DALTILE</Typography>
                    <Typography style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12 }}>
                      Last modified: 2024-11-02
                    </Typography>
                  </div>
                  <Button variant="outlined" size="small" style={{ color: '#fff', borderColor: '#fff', fontSize: 11 }} disabled>
                    Keep this record
                  </Button>
                </div>
                <div style={{ padding: '0 16px 16px' }}>
                  {PRODUCT_B_FIELDS.map(([label, value]) => (
                    <div key={label} className={classes.fieldRow}>
                      <div className={classes.fieldLabel}>{label}</div>
                      <Typography variant="body2">{value}</Typography>
                    </div>
                  ))}
                </div>
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions style={{ padding: '12px 20px' }} id="tour-dup-merge-btn">
          <Button variant="outlined" disabled>Cancel</Button>
          <Button variant="outlined" disabled>Not a Duplicate</Button>
          <Button
            variant="contained"
            disabled
            style={{ backgroundColor: '#FF682C', color: '#fff', marginLeft: 8 }}
          >
            Confirm Merge
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
